import { type FC, useMemo, useState, useCallback, useRef, useEffect } from "react";

import { ArrowRight, Check, Loader2, AlertTriangle } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { trackEvent, EventType } from "@/shared/utils/core";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useAuthorityContext } from "@/shared/hooks/useAuthorityContext";
import { parseConsentEntries, parseJsonIds } from "@/shared/utils/consentUtils";
import {
  getRequiredAdminForms,
  ADMIN_FORM_NAMES,
  ADMIN_FORM_URLS,
} from "@/shared/constants/adminForms";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { FormRowItem } from "./FormRowItem";

export const AdminResultsScreen: FC<FlowCustomComponentProps> = ({ answers }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { county } = useAuthorityContext(estateId);
  const { contacts, isLoading: contactsLoading } = useUserContacts();

  const decedentName = person?.first_name ?? "the decedent";
  const countyName = county ?? "New York";

  const adminMode = (answers["admin_confirm_admin_mode"] as string) ?? "sole";
  const isSomeoneElse = adminMode === "someone_else";
  const proposedAdminName = isSomeoneElse
    ? ((answers["admin_someone_else_full_name"] as string) ?? "the administrator").split(" ")[0]
    : "You";

  const topPersonId = answers["admin_confirm_admin_top_person_id"] as string | undefined;
  const proposedAdminTier = useMemo(() => {
    if (topPersonId) {
      const found = contacts.find((c) => String(c.id) === topPersonId);
      if (found?.priority_tier != null) return found.priority_tier;
    }
    const distributees = contacts.filter((c) => c.is_distributee && c.can_serve);
    if (distributees.length === 0) return null;
    return Math.min(...distributees.map((c) => c.priority_tier ?? 99));
  }, [contacts, topPersonId]);

  const consents = useMemo(() => parseConsentEntries(answers["admin_signoff_consents"]), [answers]);
  const coApplicantIds = useMemo(
    () => adminMode === "co" ? parseJsonIds(answers["admin_confirm_admin_co_applicants"]) : [],
    [adminMode, answers],
  );

  const { forms, helplineCards } = useMemo(
    () =>
      getRequiredAdminForms({
        contacts,
        adminMode,
        proposedAdminName: isSomeoneElse ? proposedAdminName : "You",
        proposedAdminTier,
        consents,
        coApplicantIds,
        extendedFamilyHeirship: false,
      }),
    [contacts, adminMode, proposedAdminName, proposedAdminTier, consents, coApplicantIds, isSomeoneElse],
  );

  const hasNotice = forms.some((f) => f.formId === "A-3");
  const hasMailingAffidavit = forms.some((f) => f.formId === "A-4");

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current || forms.length === 0 || contactsLoading) return;
    trackedRef.current = true;
    trackEvent(EventType.ADMIN_STEP_COMPLETED, {
      estate_id: estateId,
      step_id: "admin_forms_generated",
      form_count: forms.length,
      form_ids: forms.map((f) => f.formId),
    });
  }, [forms, estateId]);

  const uniqueFormIds = useMemo(() => [...new Set(forms.map((f) => f.formId))], [forms]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPacket = useCallback(async () => {
    if (isDownloading || uniqueFormIds.length === 0) return;
    setIsDownloading(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const fetches = uniqueFormIds.map(async (formId) => {
        const url = ADMIN_FORM_URLS[formId];
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${formId}`);
        const blob = await res.blob();
        zip.file(`${formId} ${ADMIN_FORM_NAMES[formId]}.pdf`, blob);
      });
      await Promise.all(fetches);
      const content = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(content);
      link.download = `${decedentName}-administration-forms.zip`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (err) {
      console.error("Failed to generate zip:", err);
      enqueueSnackbar("Couldn't download forms. Please try again.", { variant: "error" });
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, uniqueFormIds, decedentName]);

  if (contactsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex flex-1 flex-col gap-4 min-w-0">
          <span className="inline-flex self-start items-center gap-1 rounded-full border border-brand bg-brand-subtle px-2.5 py-0.5">
            <Check className="size-3 text-brand" aria-hidden="true" />
            <span className="text-sm font-medium text-brand">Your results</span>
          </span>
          <div className="flex flex-col gap-2">
            <p className="font-sans text-xl font-semibold leading-[30px]">
              {decedentName}'s administration forms are ready
            </p>
            <p className="text-sm text-secondary">
              Based on your answers, we prepared the {forms.length}{" "}
              {forms.length === 1 ? "form" : "forms"} Surrogate's Court in {countyName} County needs.{" "}
              {hasNotice
                ? `Before you file, you'll also mail a notice to ${decedentName}'s family. Here's how.`
                : "Review each one, then sign where shown."}
            </p>
          </div>
        </div>
        <Button
          variant="brand"
          className="shrink-0 gap-2"
          disabled={isDownloading}
          onClick={handleDownloadPacket}
        >
          {isDownloading ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Preparing...
            </>
          ) : (
            <>
              Download packet
              <ArrowRight className="size-4" aria-hidden="true" />
            </>
          )}
        </Button>
      </div>

      <div className="rounded-lg border border-primary overflow-hidden">
        <div className="bg-white p-4 border-b border-primary">
          <span className="text-sm font-medium leading-[1.55]">
            Your packet · {forms.length} {forms.length === 1 ? "form" : "forms"}
          </span>
        </div>
        {forms.map((row, i) => (
          <FormRowItem
            key={`${row.formId}-${row.signerName ?? i}`}
            formId={row.formId}
            signerLabel={row.signerLabel}
            isPersonSigner={!!row.signerName}
            citedPersons={row.citedPersons}
            noticeRecipients={row.noticeRecipients}
          />
        ))}
      </div>

      {helplineCards.length > 0 && (
        <div className="flex flex-col gap-3">
          {helplineCards.map((card) => (
            <div key={card.type} className="flex items-start gap-3 rounded-lg border border-primary bg-white p-4">
              <AlertTriangle className="size-5 shrink-0 mt-0.5 text-secondary" aria-hidden="true" />
              <div className="flex flex-col gap-0.5">
                <p className="text-sm font-medium">{card.title}</p>
                <p className="text-sm text-secondary">{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-primary bg-white p-6 flex flex-col gap-4">
        <p className="text-base font-semibold">Bring to the courthouse</p>
        <div className="flex flex-col gap-3">
          {[
            "The signed packet",
            ...(hasMailingAffidavit ? ["Your signed affidavit of mailing"] : []),
            "A certified death certificate",
            "Filing fee ($215-$625, set by estate size)",
          ].map((item) => (
            <div key={item} className="flex flex-row items-center gap-3">
              <Check className="size-4 text-brand shrink-0" aria-hidden="true" />
              <p className="text-sm text-secondary">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
